/**
 * IraTransactionCodesPage — Reference Data · IRA Transaction Codes.
 *
 * The exact Core Director transaction-code chain that runs for each IRA
 * scenario (in-house transfer, SIMPLE contribution, rollover, RMD, etc.).
 * Each scenario is one ordered list of TC steps, keyed by the composite
 * PK (Scenario, SequenceIndex). The IRA wizard reads this catalog and
 * fires each step's transaction code against Core Director in order, so
 * the code + ordering is IRS/posting-sensitive and must match the
 * authoritative TC chains Emily / the IRA team publish.
 *
 * Per Bill's 2026-04-25 Q4/Q5 sign-off the catalog ships intentionally
 * EMPTY at v3.0 launch (seed 10-ira-transaction-codes.sql has zero rows);
 * the runtime friendly-fails with "Transaction code sequence pending —
 * contact admin" until rows exist for a scenario.
 *
 * Contract (verified against the backend 2026-06-16 — shared-types model,
 * config read route, upsert DTO, delete route, and service all share
 * identical camelCase field names; NO drift found):
 *   - Read:   GET /v3/config/ira-transaction-codes
 *             → V3ConfigFunction.IraTransactionCodes, wrapped by Ok() as
 *             { success, data: IraTransactionCode[] } so the typed
 *             config read's declared ApiResult<ReadonlyArray<...>> matches
 *             the wire exactly. Returns ACTIVE rows only
 *             (.Where(t => t.IsActive)), ordered by (Scenario,
 *             SequenceIndex). Inactive rows are NOT surfaced.
 *   - Write:  POST /v3/admin/ira-transaction-codes
 *             → V3AdminFunction.IraTransactionCodesUpsert (EF createOrUpdate,
 *             composite key (Scenario, SequenceIndex)). The service persists
 *             EVERY field on both the create and update branches — no silent
 *             drop. Scenario + TransactionCode are upper-cased server-side.
 *   - Delete: DELETE /v3/admin/ira-transaction-codes/{scenario}/{sequenceIndex}
 *             → V3AdminFunction.IraTransactionCodesDelete. This is a HARD
 *             EF-Remove of the row (not a soft toggle).
 *
 * DELETE CHOICE — HARD delete (admin.iraTransactionCodes.remove):
 *   The config READ route filters `WHERE t.IsActive`, so a soft-deleted
 *   (isActive=false) row would silently vanish from this editor with no
 *   way to reactivate it. To stay honest about "the row is gone" we wire
 *   the Delete action to the HARD delete route. `isActive` remains fully
 *   editable in the modal so an admin can pause a step (skipped at runtime,
 *   stays visible) WITHOUT deleting it — note that saving isActive=false
 *   makes the row disappear on next read, which the modal hint calls out.
 *   TC steps are pure config (no historical IraDistribution/1099-R FK
 *   references the way reason codes have), so a hard delete orphans nothing.
 *
 * Migrated 2026-06-16 from ROP-teams Admin
 * (apps/ROP-teams/src/modules/Admin/editors/iraTransactionCodesEditor.tsx —
 * the operational app writes no config). Field names + write/read/delete
 * routes are the verbatim verified wire contract.
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type IraTransactionCodeUpsertBody,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, NumberField, BoolField, TextArea } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

interface TcRow {
  scenario: string;
  sequenceIndex: number;
  transactionCode: string;
  displayName: string;
  description: string | null;
  requiresAdminReview: boolean;
  isActive: boolean;
}

interface EditDraft extends TcRow {
  isNew: boolean;
}

/** Reference scenarios admin will likely create (datalist suggestions). */
const SUGGESTED_SCENARIOS: ReadonlyArray<{ code: string; description: string }> = [
  { code: 'IN_HOUSE_TRANSFER', description: 'In-house IRA-to-IRA transfer (Q4)' },
  { code: 'SIMPLE_CONTRIBUTION', description: 'SIMPLE IRA contribution (Q5)' },
  { code: 'INHERITED_TRANSFER', description: 'Beneficiary inherited transfer' },
  { code: 'DIRECT_ROLLOVER', description: '401(k)/403(b) → IRA direct rollover' },
  { code: '60_DAY_ROLLOVER', description: 'Indirect 60-day rollover' },
  { code: 'RMD_PAYMENT', description: 'Required Minimum Distribution payment' },
  { code: 'DEATH_DISTRIBUTION', description: 'Owner death distribution' },
];

const SCENARIO_PATTERN = /^[A-Z0-9_]{1,32}$/;
const TC_PATTERN = /^[A-Z0-9]{1,8}$/;

function emptyDraft(): EditDraft {
  return {
    scenario: '',
    sequenceIndex: 0,
    transactionCode: '',
    displayName: '',
    description: '',
    requiresAdminReview: false,
    isActive: true,
    isNew: true,
  };
}

function rowToDraft(r: TcRow): EditDraft {
  return { ...r, description: r.description ?? '', isNew: false };
}

function draftToUpsertBody(d: EditDraft): IraTransactionCodeUpsertBody {
  return {
    scenario: d.scenario.trim(),
    sequenceIndex: d.sequenceIndex,
    transactionCode: d.transactionCode.trim(),
    displayName: d.displayName.trim(),
    description: d.description && d.description.trim() ? d.description.trim() : null,
    requiresAdminReview: d.requiresAdminReview,
    isActive: d.isActive,
  };
}

export function IraTransactionCodesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<TcRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TcRow | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        const resp = await client.retail.config.iraTransactionCodes();
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          const mapped: TcRow[] = resp.data.map((r) => ({
            scenario: r.scenario,
            sequenceIndex: r.sequenceIndex,
            transactionCode: r.transactionCode,
            displayName: r.displayName,
            description: r.description ?? null,
            requiresAdminReview: r.requiresAdminReview,
            isActive: r.isActive,
          }));
          setRows(mapped);
        } else {
          setLoadError(resp.error ?? 'Failed to load IRA transaction codes.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const visible = useMemo(() => {
    const sorted = (rows ?? [])
      .slice()
      .sort(
        (a, b) =>
          a.scenario.localeCompare(b.scenario) || a.sequenceIndex - b.sequenceIndex,
      );
    const needle = filter.trim().toLowerCase();
    if (!needle) return sorted;
    return sorted.filter(
      (r) =>
        r.scenario.toLowerCase().includes(needle) ||
        r.transactionCode.toLowerCase().includes(needle) ||
        r.displayName.toLowerCase().includes(needle),
    );
  }, [rows, filter]);

  const scenarioCount = useMemo(
    () => new Set((rows ?? []).map((r) => r.scenario)).size,
    [rows],
  );

  /** Next free sequence index within a scenario (max + 1, or 0 when none). */
  const nextSeqFor = useCallback(
    (scenario: string): number => {
      const seqs = (rows ?? [])
        .filter((r) => r.scenario === scenario)
        .map((r) => r.sequenceIndex);
      return seqs.length ? Math.max(...seqs) + 1 : 0;
    },
    [rows],
  );

  const upsert = useCallback(
    async (d: EditDraft): Promise<boolean> => {
      const resp = await client.retail.admin.iraTransactionCodes.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToUpsertBody(d),
      );
      if (!resp.success) throw new Error(resp.error ?? 'admin upsert returned success=false');
      return true;
    },
    [client],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    const scenario = draft.scenario.trim();
    if (!scenario || !SCENARIO_PATTERN.test(scenario)) {
      setSaveError('Scenario must be 1–32 chars (uppercase letters, digits, underscore).');
      return;
    }
    if (draft.sequenceIndex < 0 || draft.sequenceIndex > 9999) {
      setSaveError('Sequence index must be 0–9999.');
      return;
    }
    const tc = draft.transactionCode.trim();
    if (!tc || !TC_PATTERN.test(tc)) {
      setSaveError('Transaction code must be 1–8 chars (uppercase letters, digits).');
      return;
    }
    if (!draft.displayName.trim()) {
      setSaveError('Plain-language step name is required.');
      return;
    }
    // Guard the composite key on create so we don't silently overwrite a
    // sibling step that already occupies (scenario, sequenceIndex).
    if (
      draft.isNew &&
      (rows ?? []).some(
        (r) => r.scenario === scenario && r.sequenceIndex === draft.sequenceIndex,
      )
    ) {
      setSaveError(
        `Step #${draft.sequenceIndex} already exists in ${scenario}. Pick a different sequence index or edit that step.`,
      );
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await upsert({ ...draft, scenario, transactionCode: tc });
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Added step #${draft.sequenceIndex} to ${scenario}.`
          : `Updated step #${draft.sequenceIndex} of ${scenario}.`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, rows, upsert, refresh]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      // HARD delete: the config read filters WHERE IsActive, so a soft toggle
      // would orphan the row out of the editor. TC steps carry no historical
      // FK, so removing the row outright is honest and safe.
      const resp = await client.retail.admin.iraTransactionCodes.remove(
        ADMIN_AUTH_TOKEN_SENTINEL,
        deleteTarget.scenario,
        deleteTarget.sequenceIndex,
      );
      if (!resp.success) throw new Error(resp.error ?? 'admin delete returned success=false');
      setToast({
        tone: 'warn',
        text: `Removed step #${deleteTarget.sequenceIndex} (${deleteTarget.displayName}) from ${deleteTarget.scenario}.`,
      });
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [deleteTarget, client, refresh]);

  const columns: ReadonlyArray<DataTableColumn<TcRow>> = useMemo(
    () => [
      {
        header: 'Scenario',
        cell: (r) => <code className="cell-mono--bold">{r.scenario}</code>,
        sortKey: (r) => r.scenario,
      },
      {
        header: 'Step',
        cell: (r) => <span className="num">{r.sequenceIndex}</span>,
        align: 'right',
        sortKey: (r) => r.sequenceIndex,
      },
      {
        header: 'TC',
        cell: (r) => <code className="cell-mono">{r.transactionCode}</code>,
        sortKey: (r) => r.transactionCode,
      },
      {
        header: 'Step name',
        cell: (r) => (
          <>
            <span className="cell-strong">{r.displayName}</span>
            {r.description && <div className="cell-desc">{r.description}</div>}
          </>
        ),
      },
      {
        header: 'Admin review?',
        cell: (r) =>
          r.requiresAdminReview ? (
            <Pill tone="warn">four-eyes</Pill>
          ) : (
            <Pill tone="muted">no</Pill>
          ),
      },
      {
        header: 'Status',
        cell: (r) => (r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>),
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft(rowToDraft(r)); setSaveError(null); }}>Edit</Button>
            <Button onClick={() => { setDeleteTarget(r); setSaveError(null); }}>Delete</Button>
          </div>
        ),
      },
    ],
    [],
  );

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load IRA transaction codes: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading IRA transaction codes…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="info">
        Each scenario is one ordered chain of Core Director transaction codes the IRA wizard fires in
        sequence (lowest step first). The catalog ships empty at launch — until rows exist for a
        scenario, the runtime shows &ldquo;Transaction code sequence pending — contact admin&rdquo;
        instead of running an unverified chain. Confirm every TC + ordering against the authoritative
        IRA-team chain before saving.
      </Banner>

      <Card
        title={`IRA Transaction Codes — ${visible.length} of ${rows.length} step${
          rows.length === 1 ? '' : 's'
        } across ${scenarioCount} scenario${scenarioCount === 1 ? '' : 's'}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="scenario, TC, or step name…"
              />
            </div>
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + Add Step
            </Button>
          </div>
        }
      >
        <p className="cell-desc">
          The IRA wizard reads from this list (active rows only) and runs each scenario&apos;s steps
          in ascending sequence order. Steps flagged for admin review pause for a four-eyes
          confirmation before posting.
        </p>
        <DataTable<TcRow>
          columns={columns}
          rows={visible}
          rowKey={(r) => `${r.scenario}::${r.sequenceIndex}`}
          initialSort={{ columnIndex: 0, dir: 'asc' }}
          emptyState={<p className="empty">No transaction-code steps match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={
          draft?.isNew
            ? 'Add a transaction-code step'
            : `Edit step #${draft?.sequenceIndex ?? ''} of ${draft?.scenario ?? ''}`
        }
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={680}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <div className="form-row">
              <TextField
                label="Scenario"
                value={draft.scenario}
                onChange={(v) =>
                  draft.isNew &&
                  setDraft({
                    ...draft,
                    scenario: v.toUpperCase().replace(/[^A-Z0-9_]/g, '').slice(0, 32),
                    sequenceIndex: nextSeqFor(
                      v.toUpperCase().replace(/[^A-Z0-9_]/g, '').slice(0, 32),
                    ),
                  })
                }
                disabled={!draft.isNew}
                monospace
                placeholder="e.g. IN_HOUSE_TRANSFER"
                hint={
                  draft.isNew
                    ? 'Up to 32 chars (uppercase, digits, underscore). Permanent — delete + re-create to move a step to another scenario. Suggestions: IN_HOUSE_TRANSFER, SIMPLE_CONTRIBUTION, INHERITED_TRANSFER, DIRECT_ROLLOVER, 60_DAY_ROLLOVER, RMD_PAYMENT, DEATH_DISTRIBUTION.'
                    : 'Permanent — part of the composite key.'
                }
              />
              <NumberField
                label="Sequence index (0-based)"
                value={draft.sequenceIndex}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    sequenceIndex: v == null ? 0 : Math.max(0, Math.min(9999, Math.trunc(v))),
                  })
                }
                min={0}
                max={9999}
                hint="Order of this step within the scenario chain. 0 runs first. Part of the composite key — permanent for an existing step (edit changes the row at this key)."
                disabled={!draft.isNew}
              />
            </div>

            <div className="form-row">
              <TextField
                label="Transaction code"
                value={draft.transactionCode}
                onChange={(v) =>
                  setDraft({
                    ...draft,
                    transactionCode: v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8),
                  })
                }
                monospace
                placeholder="e.g. 611, 614, 632, 654"
                hint="Core Director transaction code fired for this step."
              />
            </div>

            <TextField
              label="Step name"
              value={draft.displayName}
              onChange={(v) => setDraft({ ...draft, displayName: v.slice(0, 128) })}
              placeholder="e.g. Debit source IRA — full balance"
              hint="Plain-language label shown to the FSR in the IRA wizard."
            />

            <TextArea
              label="Description"
              value={draft.description ?? ''}
              onChange={(v) => setDraft({ ...draft, description: v })}
              rows={3}
              placeholder="Optional plain-English explanation shown beneath the step name."
            />

            <BoolField
              label="Requires admin (four-eyes) review"
              value={draft.requiresAdminReview}
              onChange={(v) => setDraft({ ...draft, requiresAdminReview: v })}
              hint="When on, this step pauses for an officer + manager confirmation before posting."
            />

            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
              hint="Inactive steps are skipped at runtime. Note: the config read returns active rows only, so saving this off makes the step disappear from this list on the next refresh — use Delete to remove it for good, or leave it active to keep editing it."
            />
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={deleteTarget !== null}
        title="Remove this step?"
        onClose={() => {
          if (saving) return;
          setDeleteTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          deleteTarget && (
            <>
              <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleDelete()} disabled={saving}>
                {saving ? 'Working…' : 'Remove'}
              </Button>
            </>
          )
        }
      >
        {deleteTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We&apos;ll permanently remove step #{deleteTarget.sequenceIndex} (
              <strong>{deleteTarget.displayName}</strong>, TC{' '}
              <code className="cell-mono">{deleteTarget.transactionCode}</code>) from{' '}
              <strong>{deleteTarget.scenario}</strong>. The IRA wizard won&apos;t run it on the next
              distribution. Other steps in the chain stay in place. To pause a step instead of
              deleting it, edit it and turn off <strong>Active</strong>.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
